import { useEffect, useMemo, useState } from "react";

type LoadStatus = "In-transit" | "Delivered" | "Upcoming";
type ApiStatus = "pending" | "booked" | "in_transit" | "delivered" | "cancelled";

interface Load {
  id: string;
  name: string;
  shipmentId: number;
  status: LoadStatus;
  progress: number;
  pickupDate: string;
  deliveryDate: string;
  from: string;
  to: string;
}

interface LoadDetail {
  id: string;
  pickupDate: string;
  deliveryDate: string;
  pickupLocation: string;
  deliveryLocation: string;
  driver: string;
  truck: string;
  traveled: string;
  speed: string;
}

type ShipmentRow = {
  id: number;
  lr_number: string;
  consignor_id: number;
  receiver_id: number;
  transporter_id: number;
  status: ApiStatus;
  pickup_address: string;
  delivery_address: string;
  pickup_date: string;
  delivery_date: string;
  total_amount: number;
};

type ShipmentDetailResponse = {
  id: number;
  lr_number: string;
  status: ApiStatus;
  pickup_address: string;
  delivery_address: string;
  pickup_date: string;
  delivery_date: string;
  items: RowDataPacketLike[];
  charges: RowDataPacketLike[];
};

type RowDataPacketLike = {
  [key: string]: unknown;
};

const statusColors: Record<LoadStatus, string> = {
  "In-transit": "bg-blue-100 text-blue-600",
  "Delivered": "bg-green-100 text-green-600",
  "Upcoming": "bg-orange-100 text-orange-500",
};

const formatDate = (value?: string | Date | null): string => {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleDateString();
};

const toUiStatus = (status: ApiStatus): LoadStatus => {
  if (status === "delivered") return "Delivered";
  if (status === "in_transit" || status === "booked") return "In-transit";
  return "Upcoming";
};

const progressForStatus = (status: LoadStatus): number => {
  if (status === "Delivered") return 100;
  if (status === "In-transit") return 70;
  return 25;
};

const initials = (value: string): string => {
  const s = String(value || "").replace(/\s+/g, " ").trim();
  if (!s) return "NA";
  const parts = s.split(" ").filter(Boolean);
  const first = parts[0]?.[0] ?? "N";
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] : parts[0]?.[1];
  return `${first}${last ?? ""}`.toUpperCase();
};

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState("All Loads");
  const [loads, setLoads] = useState<Load[]>([]);
  const [selectedLoad, setSelectedLoad] = useState<string | null>(null);
  const [selectedLoadId, setSelectedLoadId] = useState<number | null>(null);
  const [showDetail, setShowDetail] = useState(true);
  const [detail, setDetail] = useState<LoadDetail | null>(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const apiUrl = process.env.REACT_APP_API_URL;
    if (!token || !apiUrl) {
      setLoading(false);
      setError("Missing token or API URL");
      return;
    }

    let mounted = true;
    const fetchShipments = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${apiUrl}/auth/consignments`);
        if (!response.ok) {
          throw new Error("Failed to fetch shipments");
        }
        const data = (await response.json()) as ShipmentRow[];
        if (!mounted) return;

        const mapped: Load[] = data.map((row) => ({
          id: row.lr_number ? row.lr_number : `#${row.id}`,
          shipmentId: row.id,
          name: `Consignment ${row.id}`,
          status: toUiStatus(row.status),
          progress: progressForStatus(toUiStatus(row.status)),
          pickupDate: formatDate(row.pickup_date),
          deliveryDate: formatDate(row.delivery_date),
          from: row.pickup_address || `Consignor ${row.consignor_id}`,
          to: row.delivery_address || `Receiver ${row.receiver_id}`,
        }));

        setLoads(mapped);
        if (mapped.length > 0) {
          setSelectedLoad(mapped[0].id);
          setSelectedLoadId(mapped[0].shipmentId);
        }
        setError(null);
      } catch (_err) {
        if (mounted) setError("Could not load shipments from API");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchShipments();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const apiUrl = process.env.REACT_APP_API_URL;
    if (!selectedLoadId || !token || !apiUrl) return;

    let mounted = true;
    const fetchDetail = async () => {
      try {
        const response = await fetch(`${apiUrl}/auth/consignments/${selectedLoadId}`);
        if (!response.ok) return;
        const data = (await response.json()) as ShipmentDetailResponse;
        if (!mounted) return;
        const totalCharges = (data.charges || []).reduce((sum, charge) => sum + Number(charge.amount || 0), 0);

        setDetail({
          id: data.lr_number ? data.lr_number : `#${data.id}`,
          pickupDate: formatDate(data.pickup_date),
          deliveryDate: formatDate(data.delivery_date),
          pickupLocation: data.pickup_address || "Pickup location unavailable",
          deliveryLocation: data.delivery_address || "Delivery location unavailable",
          driver: "Assigned by transporter",
          truck: "Vehicle assigned",
          traveled: `${Math.max(0, Number(data.items?.length || 0))} stops`,
          speed: `${Math.max(0, Math.round(totalCharges / 100))} km/h`,
        });
      } catch {
        // keep existing detail state
      }
    };

    fetchDetail();
    return () => {
      mounted = false;
    };
  }, [selectedLoadId]);

  const filteredLoads = useMemo(() => {
    let result = loads;
    if (activeTab === "In-transit") result = result.filter((l) => l.status === "In-transit");
    if (activeTab === "Delivery") result = result.filter((l) => l.status === "Delivered");
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((l) => l.id.toLowerCase().includes(q) || l.name.toLowerCase().includes(q));
    }
    return result;
  }, [loads, activeTab, search]);

  const tabs = useMemo(
    () => [
      { label: "All Loads", count: loads.length },
      { label: "In-transit", count: loads.filter((l) => l.status === "In-transit").length },
      { label: "Delivery", count: loads.filter((l) => l.status === "Delivered").length },
    ],
    [loads]
  );

  const stats = useMemo(() => {
    const inTransit = loads.filter((l) => l.status === "In-transit").length;
    const delivered = loads.filter((l) => l.status === "Delivered").length;
    const total = loads.length;
    const onTimeRate = total ? ((delivered / total) * 100).toFixed(1) : "0.0";
    return { inTransit, delivered, total, onTimeRate };
  }, [loads]);

  const downloadInvoicePdf = async (): Promise<void> => {
    const apiUrl = process.env.REACT_APP_API_URL;
    if (!apiUrl || !selectedLoadId) return;
    try {
      setDownloadingInvoice(true);
      const response = await fetch(`${apiUrl}/auth/consignments/${selectedLoadId}/invoice/pdf`);
      if (!response.ok) throw new Error("Failed to download invoice");
      const blob = await response.blob();
      const fileUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = `invoice-${selectedLoadId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(fileUrl);
    } catch {
      // silent fail for now
    } finally {
      setDownloadingInvoice(false);
    }
  };

  // Responsive logic for showing/hiding sidebar and detail on mobile
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  // Open detail sidebar on mobile when a load is picked
  useEffect(() => {
    if (selectedLoadId && window.innerWidth < 768) {
      setDetailOpen(true);
    }
  }, [selectedLoadId]);

  // Utility to handle window resize for responsiveness
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setSidebarOpen(false);
        setDetailOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="flex flex-col md:flex-row h-full min-h-[680px] bg-gray-50 font-sans overflow-hidden relative">
      {/* Mobile sidebar button */}
      <button
        className="md:hidden fixed top-1 left-1 z-30 bg-white border border-gray-200 rounded-full p-2 shadow"
        aria-label="Open sidebar"
        onClick={() => setSidebarOpen(true)}
      >
        {/* Hamburger */}
        <svg width="24" height="24" fill="none" stroke="#222" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M4 7h16M4 12h16M4 17h16" />
        </svg>
      </button>

      {/* Sidebar */}
      <div
        className={`
          fixed md:static top-0 left-0 z-40 
          w-11/12 max-w-xs md:w-[25%]
          h-full bg-white border-r border-gray-100 flex flex-col flex-shrink-0 transition-transform duration-200 
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0
        `}
        style={{ boxShadow: sidebarOpen ? "0 2px 10px rgba(0,0,0,0.12)" : undefined }}
      >
        {/* Sidebar close (mobile) */}
        <div className="md:hidden flex justify-end p-3">
          <button 
            onClick={() => setSidebarOpen(false)} 
            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"
            aria-label="Close sidebar"
          >
            <svg width="14" height="14" fill="none" stroke="#222" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="px-4 pt-5 pb-3">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-lg font-bold text-gray-900 tracking-tight">Tracking Loads</h1>
            <div className="flex items-center gap-2">
              <button className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center shadow-sm">
                <svg width="14" height="14" fill="white" viewBox="0 0 24 24"><path d="M12 22c1.1 0 2-.9 2-2H10c0 1.1.9 2 2 2zm6-6V11c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" /></svg>
              </button>
              <button className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                <svg width="14" height="14" fill="#666" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z" /></svg>
              </button>
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-xs font-bold">MR</div>
            </div>
          </div>
          <div className="relative">
            <svg className="absolute left-3 top-2.5 text-gray-400" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
            <input
              className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-100 placeholder-gray-400"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="flex gap-1 px-4 pb-3">
          {tabs.map((tab) => (
            <button
              key={tab.label}
              onClick={() => setActiveTab(tab.label)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                activeTab === tab.label ? "bg-blue-600 text-white shadow-sm" : "text-gray-500 hover:bg-gray-100"
              }`}
            >
              {tab.label}
              <span className={`text-xs rounded-full px-1.5 py-0.5 ${activeTab === tab.label ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-600"}`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-3 space-y-2 pb-4 group bw-scroll">
          {loading && <div className="text-xs text-gray-500 px-1">Loading shipments...</div>}
          {error && <div className="text-xs text-rose-500 px-1">{error}</div>}
          {!loading && !error && filteredLoads.length === 0 && (
            <div className="text-xs text-gray-500 px-1">No shipments found</div>
          )}
          {filteredLoads.map((load, i) => (
            <button
              key={`${load.id}-${i}`}
              onClick={() => {
                setSelectedLoad(load.id);
                setSelectedLoadId(load.shipmentId);
                setShowDetail(true);
                if (window.innerWidth < 768) {
                  setSidebarOpen(false);
                  setDetailOpen(true);
                }
              }}
              className={`w-full text-left p-3 rounded-xl border transition-all ${
                selectedLoad === load.id ? "border-blue-200 bg-blue-50 shadow-sm" : "border-gray-100 bg-white hover:border-gray-200 hover:shadow-sm"
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{load.name}</p>
                  <p className="text-xs text-gray-400">{load.id}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[load.status]}`}>{load.status}</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span>{load.pickupDate}</span>
                <div className="flex-1 flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" />
                  <div className="flex-1 border-t border-dashed border-gray-300" />
                  <svg width="12" height="12" fill="#94a3b8" viewBox="0 0 24 24"><path d="M17 8C8 10 5.9 16.17 3.82 21L5.71 22l1-2.3A4.49 4.49 0 0 0 8 20c4 0 4-2 8-2s4 2 8 2v-2c-4 0-4-2-8-2c-1.13 0-1.9.16-2.53.33C14.28 12.07 16 9.66 17 8z" /></svg>
                </div>
                <span>{load.deliveryDate}</span>
              </div>
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>{load.from}</span>
                <span>{load.to}</span>
              </div>

              {/* Thumbnails + progress (show on hover over overflow list) */}
              <div className="mt-3 opacity-0 max-h-0 overflow-hidden transition-all duration-200 group-hover:opacity-100 group-hover:max-h-24">
                <div className="flex items-center justify-between">
                  <div className="flex -space-x-2">
                    {[
                      { label: initials(load.from), tone: "bg-black text-white" },
                      { label: initials(load.to), tone: "bg-white text-black border border-gray-300" },
                      { label: "LR", tone: "bg-gray-200 text-gray-800" },
                    ].map((t, idx) => (
                      <div
                        key={`${t.label}-${idx}`}
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold shadow-sm ${t.tone}`}
                        title={idx === 0 ? "Pickup" : idx === 1 ? "Delivery" : "LR"}
                      >
                        {t.label}
                      </div>
                    ))}
                  </div>
                  <div className="text-[10px] text-gray-500 font-semibold">
                    {load.progress}%
                  </div>
                </div>
                <div className="mt-2 w-full h-2 rounded-full bg-gray-200 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-black transition-all duration-300"
                    style={{ width: `${load.progress}%` }}
                  />
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Overlay for closing the sidebar (mobile) */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close sidebar overlay"
        />
      )}

      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 p-5 pb-0">
          {[
            { label: "Active Loads", value: `${stats.inTransit}`, icon: "🚛", color: "bg-blue-50 border-blue-100" },
            { label: "Total Deliveries", value: `${stats.delivered}`, icon: "📦", color: "bg-orange-50 border-orange-100" },
            { label: "On-Time Rate", value: `${stats.onTimeRate}%`, icon: "⏱️", color: "bg-teal-50 border-teal-100" },
            { label: "Total Loads", value: `${stats.total}`, icon: "📊", color: "bg-green-50 border-green-100" },
          ].map((stat) => (
            <div key={stat.label} className={`rounded-2xl border p-4 ${stat.color} bg-white flex items-center gap-3 shadow-sm`}>
              <span className="text-2xl">{stat.icon}</span>
              <div>
                <p className="text-xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </div>
              <button className="ml-auto text-gray-400 hover:text-gray-600">
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 18l6-6-6-6" /></svg>
              </button>
            </div>
          ))}
        </div>

        <div className="flex-1 relative m-0 md:m-5 md:mt-4 rounded-none md:rounded-2xl overflow-hidden shadow-sm border-0 md:border md:border-gray-100">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-100 via-blue-50 to-slate-200">
            <svg width="100%" height="100%" className="opacity-30">
              <defs>
                <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#94a3b8" strokeWidth="0.5" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
            <svg className="absolute inset-0 w-full h-full opacity-40 pointer-events-none">
              <line x1="0" y1="40%" x2="100%" y2="40%" stroke="#94a3b8" strokeWidth="2" />
              <line x1="0" y1="65%" x2="100%" y2="65%" stroke="#94a3b8" strokeWidth="1.5" />
              <line x1="30%" y1="0" x2="30%" y2="100%" stroke="#94a3b8" strokeWidth="1.5" />
              <line x1="60%" y1="0" x2="60%" y2="100%" stroke="#94a3b8" strokeWidth="2" />
            </svg>
            <svg className="absolute inset-0 w-full h-full pointer-events-none">
              <polyline points="200,300 350,250 500,200 650,160" fill="none" stroke="#3b82f6" strokeWidth="3" strokeDasharray="8 4" opacity="0.8" />
            </svg>
            {/* Hide map markers on extra small screens for clarity */}
            <div className="hidden sm:block absolute" style={{ left: "58%", top: "28%" }}>
              <div className="relative">
                <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                  <div className="w-3 h-3 bg-white rounded-full" />
                </div>
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-red-500 rotate-45" />
              </div>
            </div>
            <div className="hidden sm:block absolute" style={{ left: "38%", top: "44%" }}>
              <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md border border-gray-200">
                <span className="text-sm">🚛</span>
              </div>
            </div>
            <div className="absolute top-4 left-1/2 text-xs font-semibold text-gray-500 uppercase tracking-widest hidden md:block">ALL-LINN</div>
            <div className="absolute top-16 right-1/3 text-xs text-gray-400 font-medium hidden lg:block">Rant 2</div>
            <div className="absolute top-1/3 left-1/4 text-xs font-bold text-gray-600 uppercase tracking-wide hidden md:block">SIBU LAAKULA</div>
            <div className="absolute bottom-1/4 left-1/3 text-xs text-gray-500 uppercase tracking-wide hidden lg:block">KESKLINN</div>
            <div className="absolute bottom-1/3 left-1/6 text-xs text-gray-400 hidden xl:block">MAAILM</div>
            <div className="absolute bottom-8 left-1/3 text-xs text-gray-400 italic hidden md:block">A. La Coq Arena</div>
            <div className="absolute top-4 right-4 flex flex-col gap-1 hidden md:flex">
              <button className="w-7 h-7 bg-white rounded-md shadow flex items-center justify-center text-gray-600 font-bold hover:bg-gray-50">+</button>
              <button className="w-7 h-7 bg-white rounded-md shadow flex items-center justify-center text-gray-600 font-bold hover:bg-gray-50">-</button>
            </div>
          </div>

          {/* Detail panel for desktop, modal for mobile */}
          {/* Mobile overlay for detail */}
          {detailOpen && (
            <div className="fixed inset-0 z-50 bg-black/30 flex md:hidden">
              <div className="w-full max-w-xs mx-auto my-auto bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden relative animate-fadeIn">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
                  <div>
                    <p className="text-xs text-gray-400">Load id:</p>
                    <p className="text-sm font-bold text-gray-900">{detail?.id || "-"}</p>
                  </div>
                  <button onClick={() => setDetailOpen(false)} className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200">
                    <svg width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" /></svg>
                  </button>
                </div>
                <div className="px-4 py-3 space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <p className="text-xs text-gray-400">Pick up date</p>
                      <p className="text-xs font-semibold text-gray-700">{detail?.pickupDate || "-"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Delivery date</p>
                      <p className="text-xs font-semibold text-gray-700">{detail?.deliveryDate || "-"}</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <div className="mt-1 w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-400">Pick up location</p>
                        <p className="text-xs font-medium text-gray-700">{detail?.pickupLocation || "-"}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="mt-1 w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-400">Delivery location</p>
                        <p className="text-xs font-medium text-gray-700">{detail?.deliveryLocation || "-"}</p>
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-gray-50 pt-2 space-y-2">
                    {[
                      { label: "Driver", value: detail?.driver || "-", icon: "👤" },
                      { label: "Truck", value: detail?.truck || "-", icon: "🚛" },
                      { label: "Traveled", value: detail?.traveled || "-", icon: "📍" },
                      { label: "Speed", value: detail?.speed || "-", icon: "⚡" },
                    ].map((item) => (
                      <div key={item.label} className="flex items-center justify-between">
                        <p className="text-xs text-gray-400">{item.label}</p>
                        <p className="text-xs font-semibold text-gray-700 flex items-center gap-1">
                          {item.value} <span>{item.icon}</span>
                        </p>
                      </div>
                    ))}
                  </div>
                  <div className="pt-2">
                    <button
                      onClick={downloadInvoicePdf}
                      disabled={!selectedLoadId || downloadingInvoice}
                      className="w-full text-xs px-2 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
                    >
                      {downloadingInvoice ? "Downloading..." : "Download Invoice"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Desktop detail panel */}
          {showDetail && (
            <div className="absolute top-4 left-4 w-11/12 max-w-sm md:w-64 md:max-w-xs bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-10 hidden md:block">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
                <div>
                  <p className="text-xs text-gray-400">Load id:</p>
                  <p className="text-sm font-bold text-gray-900">{detail?.id || "-"}</p>
                </div>
                <button onClick={() => setShowDetail(false)} className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200">
                  <svg width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="px-4 py-3 space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-xs text-gray-400">Pick up date</p>
                    <p className="text-xs font-semibold text-gray-700">{detail?.pickupDate || "-"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Delivery date</p>
                    <p className="text-xs font-semibold text-gray-700">{detail?.deliveryDate || "-"}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <div className="mt-1 w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-400">Pick up location</p>
                      <p className="text-xs font-medium text-gray-700">{detail?.pickupLocation || "-"}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="mt-1 w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-400">Delivery location</p>
                      <p className="text-xs font-medium text-gray-700">{detail?.deliveryLocation || "-"}</p>
                    </div>
                  </div>
                </div>
                <div className="border-t border-gray-50 pt-2 space-y-2">
                  {[
                    { label: "Driver", value: detail?.driver || "-", icon: "👤" },
                    { label: "Truck", value: detail?.truck || "-", icon: "🚛" },
                    { label: "Traveled", value: detail?.traveled || "-", icon: "📍" },
                    { label: "Speed", value: detail?.speed || "-", icon: "⚡" },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center justify-between">
                      <p className="text-xs text-gray-400">{item.label}</p>
                      <p className="text-xs font-semibold text-gray-700 flex items-center gap-1">
                        {item.value} <span>{item.icon}</span>
                      </p>
                    </div>
                  ))}
                </div>
                <div className="pt-2">
                  <button
                    onClick={downloadInvoicePdf}
                    disabled={!selectedLoadId || downloadingInvoice}
                    className="w-full text-xs px-2 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
                  >
                    {downloadingInvoice ? "Downloading..." : "Download Invoice"}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="absolute bottom-4 right-4 bg-white rounded-2xl shadow-xl border border-gray-100 p-4 w-48 z-10 hidden sm:block">
            <p className="text-xs font-semibold text-gray-500 mb-1">Week Overview</p>
            <p className="text-3xl font-bold text-blue-600">5,000</p>
            <div className="mt-2 h-10 flex items-end gap-1">
              {[40, 60, 35, 80, 55, 70, 45].map((h, i) => (
                <div key={i} className="flex-1 rounded-sm bg-blue-100" style={{ height: `${h}%` }}>
                  <div className="w-full rounded-sm bg-blue-500" style={{ height: i === 3 ? "100%" : "40%" }} />
                </div>
              ))}
            </div>
          </div>
          {/* Mobile summary widget */}
          <div className="absolute bottom-2 left-2 right-2 sm:hidden">
            <div className="bg-white rounded-xl shadow-md border border-gray-100 p-3 flex flex-col items-center min-w-0">
              <p className="text-xs font-semibold text-gray-500 mb-1">Week Overview</p>
              <p className="text-3xl font-bold text-blue-600">5,000</p>
              <div className="w-full mt-2 h-8 flex items-end gap-1">
                {[40, 60, 35, 80, 55, 70, 45].map((h, i) => (
                  <div key={i} className="flex-1 rounded-sm bg-blue-100" style={{ height: `${h}%` }}>
                    <div className="w-full rounded-sm bg-blue-500" style={{ height: i === 3 ? "100%" : "40%" }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;